import { useEffect, useState, useMemo, Children } from "react";
import { memoize } from "lodash";
import pDebounce from "p-debounce";
import RichTextInput from "ra-input-rich-text";
import { Typography, useMediaQuery, Tabs, Tab, Divider } from "@material-ui/core";
import {
  List,
  Datagrid,
  Edit,
  Create,
  TextField,
  TextInput,
  NumberField,
  NumberInput,
  SimpleFormIterator,
  ArrayInput,
  ImageField,
  ImageInput,
  FormDataConsumer,
  SimpleList,
  required,
  FormWithRedirect,
  FormInput,
  Toolbar,
  FunctionField,
  DateTimeInput,
  BooleanInput,
  useRecordContext,
  SelectArrayInput,
  TopToolbar,
  ListButton,
} from "react-admin";
import { NoInput } from "./shared/NoInput";
import ErrorBoundary from "./shared/ErrorBoundary";

const listItemsIcons = [
  { id: 1, name: "Aire Acondicionado" },
  { id: 2, name: "Piscina" },
  { id: 3, name: "Zona Wifi" },
  { id: 4, name: "Areas Verdes" },
  { id: 5, name: "Tv Smart" },
  { id: 6, name: "Restaurante" },
];

export const TourPackageList = (props) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { brandName, ...rest } = props;

  return (
    <List
      {...rest}
      exporter={false}
      title={brandName ? `Paquetes — ${brandName}` : "Lista de Paquetes"}
      sort={{ field: "relevance", order: "DESC" }}
    >
      {isSmall ? (
        <SimpleList
          primaryText={(record) => record.name}
          secondaryText={(record) =>
            `${record.capacity.min}-${record.capacity.max} personas - ${record.duration} noches`
          }
          tertiaryText={(record) => `S/.${record.price}`}
        />
      ) : (
        <Datagrid rowClick="edit">
          <FunctionField
            source="relevance"
            label="Relevancia"
            render={(record) => record.relevance || 0}
          />
          <TextField source="name" label="Nombre" />
          <NumberField source="capacity.min" label="Personas mín" />
          <NumberField source="capacity.max" label="Personas máx" />
          <NumberField source="duration" label="Noches" />
          <NumberField source="price" label="Precio" />
          <FunctionField
            label="Oferta"
            render={(record) => {
              if (record.offer?.enabled) {
                if (new Date(record.offer.from) > new Date()) {
                  return "Programado";
                } else if (new Date(record.offer.to) < new Date()) {
                  return "Expirado";
                } else {
                  return "En curso";
                }
              }
              return "Desactivado";
            }}
          />
          <FunctionField
            label="Iconos"
            render={(record) => {
              const data = [];
              record.listIcons.map((x) => {
                data.push(listItemsIcons.find((y) => y.id == x).name + " ");
              });
              return data;
            }}
          />
          <NumberField source="offer.price" label="Precio oferta" />
        </Datagrid>
      )}
    </List>
  );
};

const TourPackageTitle = ({ record, brandName }) => {
  return (
    <span>
      Paquete: {record ? record.name : ""}
      {brandName ? ` — ${brandName}` : ""}
    </span>
  );
};

const ListActions = () => (
  <TopToolbar>
    <ListButton label="Volver a la lista" />
  </TopToolbar>
);

const fetchBySlug = memoize(async (slug) => {
  // No se valida slug en cliente - se valida en el servidor
  return Promise.resolve(null);
});

const slugAvailable = pDebounce(async (value, values) => {
  const data = await fetchBySlug(value);
  if (values.id && data?.id === values.id) return;
  if (data?.id) return "Este slug ya está siendo usado";
}, 300);

// Renderiza un tab con los inputs siempre montados (solo se ocultan con CSS).
// A diferencia de TabbedForm, las pestañas NO se desmontan al navegar, así los
// valores de cada sección se conservan al ir hacia atrás o adelante.
const renderTab = (children, formProps) =>
  Children.map(children, (input) =>
    input ? (
      <FormInput
        basePath={formProps.basePath}
        input={input}
        record={formProps.record}
        resource={formProps.resource}
        variant={input.props.variant || formProps.variant}
        margin={input.props.margin || formProps.margin}
      />
    ) : null
  );

// Array vacío con identidad estable para los ArrayInput en modo crear.
// react-final-form re-registra el campo (unregister + register + notify)
// cada vez que defaultValue/initialValue cambian de identidad; un [] inline
// se recrea en cada render y eso entraba en un loop infinito de renders
// (React #185: Maximum update depth exceeded) que congelaba el formulario.
const EMPTY_ARRAY = [];

const TourPackageForm = ({ edit, ...props }) => {
  useEffect(() => {
    fetchBySlug.cache.clear();
  }, []);

  const record = useRecordContext();
  const [activeTab, setActiveTab] = useState(0);
  // "Ahora" estable por montaje del form (misma razón que EMPTY_ARRAY:
  // new Date() inline cambiaba de identidad en cada render).
  const now = useMemo(() => new Date(), []);

  return (
    <FormWithRedirect
      {...props}
      render={(formProps) => (
        <form className="tour-package-form" onSubmit={formProps.handleSubmit}>
          <Tabs
            value={activeTab}
            onChange={(event, value) => setActiveTab(value)}
          >
            <Tab label="General" />
            <Tab label="Detalles" />
            <Tab label="Imágenes" />
            <Tab label="Vídeos" />
          </Tabs>
          <Divider />
          <div
            className="tour-package-tab"
            style={activeTab !== 0 ? { display: "none" } : undefined}
          >
            {renderTab(
              [
                edit && <TextInput disabled source="id" />,
                <TextInput
                  source="slug"
                  label="Slug"
                  onKeyPress={(e) => !/[a-z\d-]/.test(e.key) && e.preventDefault()}
                  validate={[required(), slugAvailable]}
                />,
                <SelectArrayInput
                  label="Iconos"
                  source="listIcons"
                  choices={listItemsIcons}
                />,
                <NumberInput
                  source="relevance"
                  label="Relevancia"
                  defaultValue={record?.relevance}
                  validate={[required()]}
                  step={1}
                />,
                <TextInput source="name" label="Nombre" validate={required()} />,
                <NumberInput
                  source="capacity.min"
                  label="Capacidad mínima"
                  validate={required()}
                />,
                <NumberInput
                  source="capacity.max"
                  label="Capacidad máxima"
                  validate={required()}
                />,
                <NumberInput
                  source="duration"
                  label="Cantidad de noches"
                  validate={required()}
                />,
                <NumberInput source="price" label="Precio" validate={required()} />,
                <NoInput>
                  <Typography variant="h5" style={{ marginTop: 16 }}>
                    Oferta
                  </Typography>
                </NoInput>,
                <BooleanInput
                  source="offer.enabled"
                  label="Activo"
                  defaultValue={false}
                />,
                <NumberInput
                  source="offer.price"
                  label="Precio oferta"
                  defaultValue={record?.price}
                />,
                <DateTimeInput
                  source="offer.from"
                  label="Válido desde"
                  defaultValue={now}
                />,
                <DateTimeInput
                  source="offer.to"
                  label="Válido hasta"
                  defaultValue={now}
                />,
                <BooleanInput
                  source="PackSelling"
                  label="Mas Vendidos"
                  defaultValue={false}
                />,
              ],
              formProps
            )}
          </div>
          <div
            className="tour-package-tab"
            style={activeTab !== 1 ? { display: "none" } : undefined}
          >
            {renderTab(
              [
                // El boundary va FUERA del ArrayInput: dentro del
                // SimpleFormIterator rompería el prefijo de source
                // (el input quedaría registrado como `description` suelto
                // en vez de `details[N].description` y nunca mostraría
                // ni guardaría el valor).
                <ErrorBoundary
                  fallback={
                    <Typography color="error">
                      No se pudo cargar la sección Detalles. Recargá la
                      página para reintentar.
                    </Typography>
                  }
                >
                  <ArrayInput
                    source="details"
                    label=""
                    defaultValue={edit ? undefined : EMPTY_ARRAY}
                  >
                    <SimpleFormIterator>
                      <TextInput
                        source="title"
                        label="Título"
                        validate={required()}
                      />
                      <RichTextInput
                        source="description"
                        label="Descripción"
                        validation={required()}
                      />
                    </SimpleFormIterator>
                  </ArrayInput>
                </ErrorBoundary>,
              ],
              formProps
            )}
          </div>
          <div
            className="tour-package-tab"
            style={activeTab !== 2 ? { display: "none" } : undefined}
          >
            {renderTab(
              [
                <ArrayInput
                  source="images"
                  defaultValue={edit ? undefined : EMPTY_ARRAY}
                  label=""
                >
                  <SimpleFormIterator>
                    <ImageInput
                      format={(v) =>
                        !v || typeof v === "string" ? { src: v } : v
                      }
                      label="Fotografía"
                      accept="image/*"
                    >
                      <ImageField source="src" />
                    </ImageInput>
                  </SimpleFormIterator>
                </ArrayInput>,
              ],
              formProps
            )}
          </div>
          <div
            className="tour-package-tab"
            style={activeTab !== 3 ? { display: "none" } : undefined}
          >
            {renderTab(
              [
                <ArrayInput
                  source="videos"
                  label=""
                  defaultValue={edit ? undefined : EMPTY_ARRAY}
                >
                  <SimpleFormIterator>
                    <TextInput
                      source=""
                      label="Youtube Video ID"
                    />
                    <FormDataConsumer>
                      {({ scopedFormData, getSource }) =>
                        scopedFormData?.length === 11 && (
                          <div key={getSource("")}>
                            <iframe
                              title={scopedFormData}
                              style={{ maxWidth: "100%" }}
                              width="560"
                              height="315"
                              src={`https://www.youtube.com/embed/${scopedFormData}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        )
                      }
                    </FormDataConsumer>
                  </SimpleFormIterator>
                </ArrayInput>,
              ],
              formProps
            )}
          </div>
          <Toolbar
            basePath={formProps.basePath}
            handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
            handleSubmit={formProps.handleSubmit}
            invalid={formProps.invalid}
            mutationMode={formProps.mutationMode}
            pristine={formProps.pristine}
            record={formProps.record}
            redirect={formProps.redirect}
            resource={formProps.resource}
            saving={formProps.saving}
            submitOnEnter={formProps.submitOnEnter}
            undoable={formProps.undoable}
            validating={formProps.validating}
          />
        </form>
      )}
    />
  );
};

export const TourPackageEdit = ({ brandName, ...props }) => (
  <Edit
    title={<TourPackageTitle brandName={brandName} />}
    actions={<ListActions />}
    {...props}
  >
    <TourPackageForm edit />
  </Edit>
);

export const TourPackageCreate = ({ brandName, ...props }) => (
  <Create
    title={brandName ? `Crear paquete — ${brandName}` : "Crear un paquete"}
    actions={<ListActions />}
    {...props}
  >
    <TourPackageForm />
  </Create>
);
